import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  getNationalities as getNationalitiesApi,
  getNationalMinorities as getNationalMinoritiesApi,
  getProfessions as getProfessionsApi,
  getProfessionalQualifications as getProfessionalQualificationsApi,
  getParentshipTypes as getParentshipTypesApi,
  getParentNumbers as getParentNumbersApi,
  getCounties as getCountiesApi,
  getTowns as getTownsApi,
  searchTowns as searchTownsApi,
  getProgramTypes as getProgramTypesApi,
  getPrograms as getProgramsApi,
  getInstitutionPrograms as getInstitutionProgramsApi,
  getProgramAges as getProgramAgesApi,
  getInstitutionProgramAges as getInstitutionProgramAgesApi,
  getProgramDurations as getProgramDurationsApi,
  getInstitutionProgramDurations as getInstitutionProgramDurationsApi,
  getRequestMunicipalitiesSearch as getRequestMunicipalitiesSearchApi,
  getMunicipality as getMunicipalityApi,
  getRequestStatuses as getRequestStatusesApi,
  getMonths as getMonthsApi,
  getRelationships as getRelationshipsApi,
  getGroupInstitutions as getGroupInstitutionsApi,
  getInstitutionProgramsFilter as getInstitutionProgramsFilterApi,
  getDocumentTypes as getDocumentTypesApi,
  getReasonNonExistenceOibTypes as getReasonNonExistenceOibTypesApi,
  getSexes as getSexesApi,
} from "../api/kindergartenApi";
import MunicipalityWithCounty from "../models/Municipality/MunicipalityWithCounty";
import RequestStatus from "../models/Request/RequestStatus";
import CodeBook from "../models/Shared/CodeBook";
import Town from "../models/Town/Town";

interface CodeBookState {
  nationalities: CodeBook[];
  nationalMinorities: CodeBook[];
  professions: CodeBook[];
  professionalQualifications: CodeBook[];
  parentshipTypes: CodeBook[];
  parentNumbers: CodeBook[];
  counties: CodeBook[];
  towns: Town[];
  programTypes: CodeBook[];
  programs: CodeBook[];
  filteredPrograms: CodeBook[];
  programAges: CodeBook[];
  filteredProgramAges: CodeBook[];
  programDurations: CodeBook[];
  filteredProgramDurations: CodeBook[];
  municipalities: MunicipalityWithCounty[];
  requestStatuses: RequestStatus[];
  months: CodeBook[];
  relationships: CodeBook[];
  groupInstitutions: CodeBook[];
  documentTypes: CodeBook[];
  reasonNonExistenceOibTypes: CodeBook[];
  sexes: CodeBook[];
}

let initialState: CodeBookState = {
  nationalities: new Array<CodeBook>(),
  nationalMinorities: new Array<CodeBook>(),
  professions: new Array<CodeBook>(),
  professionalQualifications: new Array<CodeBook>(),
  parentshipTypes: new Array<CodeBook>(),
  parentNumbers: new Array<CodeBook>(),
  counties: new Array<CodeBook>(),
  towns: new Array<Town>(),
  programTypes: new Array<CodeBook>(),
  programs: new Array<CodeBook>(),
  filteredPrograms: new Array<CodeBook>(),
  programAges: new Array<CodeBook>(),
  filteredProgramAges: new Array<CodeBook>(),
  programDurations: new Array<CodeBook>(),
  filteredProgramDurations: new Array<CodeBook>(),
  municipalities: new Array<MunicipalityWithCounty>(),
  requestStatuses: new Array<RequestStatus>(),
  months: new Array<CodeBook>(),
  relationships: new Array<CodeBook>(),
  groupInstitutions: new Array<CodeBook>(),
  documentTypes: new Array<CodeBook>(),
  reasonNonExistenceOibTypes: new Array<CodeBook>(),
  sexes: new Array<CodeBook>(),
};

export const getNationalities = createAsyncThunk(
  "codeBook/getNationalities",
  async () => {
    const response = await getNationalitiesApi();
    return response;
  }
);

export const getNationalMinorities = createAsyncThunk(
  "codeBook/getNationalMinorities",
  async () => {
    const response = await getNationalMinoritiesApi();
    return response;
  }
);

export const getProfessions = createAsyncThunk(
  "codeBook/getProfessions",
  async () => {
    const response = await getProfessionsApi();
    return response;
  }
);

export const getProfessionalQualifications = createAsyncThunk(
  "codeBook/getProfessionalQualifications",
  async () => {
    const response = await getProfessionalQualificationsApi();
    return response;
  }
);

export const getParentshipTypes = createAsyncThunk(
  "codeBook/getParentshipTypes",
  async () => {
    const response = await getParentshipTypesApi();
    return response;
  }
);

export const getParentNumbers = createAsyncThunk(
  "codeBook/getParentNumbers",
  async () => {
    const response = await getParentNumbersApi();
    return response;
  }
);

export const getCounties = createAsyncThunk(
  "codeBook/getCounties",
  async () => {
    const response = await getCountiesApi();
    return response;
  }
);

export const getTowns = createAsyncThunk("codeBook/getTowns", async () => {
  const response = await getTownsApi();
  return response;
});

export const searchTowns = createAsyncThunk(
  "codeBook/getTowns",
  async (countyId: string) => {
    const response = await searchTownsApi(countyId);
    return response;
  }
);

export const getProgramTypes = createAsyncThunk(
  "codeBook/geProgramTypes",
  async () => {
    const response = await getProgramTypesApi();
    return response;
  }
);

export const getPrograms = createAsyncThunk(
  "codeBook/getPrograms",
  async () => {
    const response = await getProgramsApi();
    return response;
  }
);

export const getInstitutionPrograms = createAsyncThunk(
  "codeBook/searchPrograms",
  async (pedagogicalYearInstitutionId: number) => {
    const response = await getInstitutionProgramsApi(
      pedagogicalYearInstitutionId
    );
    return response;
  }
);

export const getInstitutionProgramsFilter = createAsyncThunk(
  "codeBook/searchPrograms",
  async ({
    pedagogicalYearInstitutionId,
    parentIntitutionId,
  }: {
    pedagogicalYearInstitutionId: number;
    parentIntitutionId?: number;
  }) => {
    const response = await getInstitutionProgramsFilterApi(
      pedagogicalYearInstitutionId,
      parentIntitutionId
    );
    return response;
  }
);

export const getGroupInstitutions = createAsyncThunk(
  "codeBook/searchGroupInstitutions",
  async (pedagogicalYearInstitutionId: number) => {
    const response = await getGroupInstitutionsApi(
      pedagogicalYearInstitutionId
    );
    return response;
  }
);

export const getProgramAges = createAsyncThunk(
  "codeBook/getProgramAges",
  async () => {
    const response = await getProgramAgesApi();
    return response;
  }
);

export const getInstitutionProgramAges = createAsyncThunk(
  "codeBook/searchProgramAges",
  async (pedagogicalYearInstitutionId: number) => {
    const response = await getInstitutionProgramAgesApi(
      pedagogicalYearInstitutionId
    );
    return response;
  }
);

export const getProgramDurations = createAsyncThunk(
  "codeBook/getProgramDurations",
  async () => {
    const response = await getProgramDurationsApi();
    return response;
  }
);

export const getInstitutionProgramDurations = createAsyncThunk(
  "codeBook/searchProgramDurations",
  async (pedagogicalYearInstitutionId: number) => {
    const response = await getInstitutionProgramDurationsApi(
      pedagogicalYearInstitutionId
    );
    return response;
  }
);

export const getRequestMunicipalitiesSearch = createAsyncThunk(
  "codeBook/getMunicipalitiesSearch",
  async ({ name, countyId }: { name: string; countyId?: number }) => {
    const response = await getRequestMunicipalitiesSearchApi(name, countyId);
    return response;
  }
);

export const getMunicipalityById = createAsyncThunk(
  "codeBook/getMunicipalityById",
  async ({ municipalityId }: { municipalityId?: number }) => {
    const response = await getMunicipalityApi(municipalityId);
    return response;
  }
);

export const getRequestStatuses = createAsyncThunk(
  "request/getRequestStatuses",
  async () => {
    const response = await getRequestStatusesApi();
    return response;
  }
);

export const getMonths = createAsyncThunk("codeBook/getMonths", async () => {
  const response = await getMonthsApi();
  return response;
});

export const getRelationships = createAsyncThunk(
  "codeBook/getRelationships",
  async () => {
    const response = await getRelationshipsApi();
    return response;
  }
);

export const getDocumentTypes = createAsyncThunk(
  "codeBook/getDocumentTypes",
  async () => {
    const response = await getDocumentTypesApi();
    return response;
  }
);

export const getReasonNonExistenceOibTypes = createAsyncThunk(
  "codeBook/getReasonNonExistenceOibTypes",
  async () => {
    const response = await getReasonNonExistenceOibTypesApi();
    return response;
  }
);

export const getSexes = createAsyncThunk("codeBook/getSexes", async () => {
  const response = await getSexesApi();
  return response;
});

const codeBookSlice = createSlice({
  name: "codeBook",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getNationalities.fulfilled, (state, { payload }) => {
        state.nationalities = payload;
      })
      .addCase(getNationalMinorities.fulfilled, (state, { payload }) => {
        state.nationalMinorities = payload;
      })
      .addCase(getProfessions.fulfilled, (state, { payload }) => {
        state.professions = payload;
      })
      .addCase(
        getProfessionalQualifications.fulfilled,
        (state, { payload }) => {
          state.professionalQualifications = payload;
        }
      )
      .addCase(getParentshipTypes.fulfilled, (state, { payload }) => {
        state.parentshipTypes = payload;
      })
      .addCase(getParentNumbers.fulfilled, (state, { payload }) => {
        state.parentNumbers = payload;
      })
      .addCase(getCounties.fulfilled, (state, { payload }) => {
        state.counties = payload;
      })
      .addCase(getMonths.fulfilled, (state, { payload }) => {
        state.months = payload;
      })
      .addCase(getRelationships.fulfilled, (state, { payload }) => {
        state.relationships = payload;
      })
      .addCase(getTowns.fulfilled, (state, { payload }) => {
        state.towns = payload;
      })
      .addCase(getProgramTypes.fulfilled, (state, { payload }) => {
        state.programTypes = payload;
      })
      .addCase(getPrograms.fulfilled, (state, { payload }) => {
        state.programs = payload;
      })
      .addCase(getInstitutionPrograms.fulfilled, (state, { payload }) => {
        state.filteredPrograms = payload;
      })
      .addCase(getProgramAges.fulfilled, (state, { payload }) => {
        state.programAges = payload;
      })
      .addCase(getInstitutionProgramAges.fulfilled, (state, { payload }) => {
        state.filteredProgramAges = payload;
      })
      .addCase(getProgramDurations.fulfilled, (state, { payload }) => {
        state.programDurations = payload;
      })
      .addCase(
        getInstitutionProgramDurations.fulfilled,
        (state, { payload }) => {
          state.filteredProgramDurations = payload;
        }
      )
      .addCase(getRequestStatuses.fulfilled, (state, { payload }) => {
        state.requestStatuses = payload;
      })
      .addCase(getGroupInstitutions.fulfilled, (state, { payload }) => {
        state.groupInstitutions = payload;
      })
      .addCase(getDocumentTypes.fulfilled, (state, { payload }) => {
        state.documentTypes = payload;
      })
      .addCase(
        getReasonNonExistenceOibTypes.fulfilled,
        (state, { payload }) => {
          state.reasonNonExistenceOibTypes = payload;
        }
      )
      .addCase(getSexes.fulfilled, (state, { payload }) => {
        state.sexes = payload;
      });
  },
});

export default codeBookSlice.reducer;
